import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, AlertCircle, CheckCircle2, XCircle, Clock, Gem, Info, AtSign } from 'lucide-react'
import toast from 'react-hot-toast'
import useStarsWithdrawStore from '../store/useStarsWithdrawStore'
import { starsToCrystals, normalizeUsername, isValidUsername } from '../services/starsWithdrawApi'
import useAppStore from '../../../store/useAppStore'
import { transactionApi } from '../../../services'

// Быстрый выбор количества звёзд
const QUICK_STARS = [50, 100, 500, 1000]

const formatNumber = (value) => Number(value).toLocaleString('en-US')

const StarIcon = ({ className = 'w-4 h-4' }) => (
  <img src="/images/payment/star.png" alt="" aria-hidden="true" className={`inline-block ${className}`} />
)

/**
 * StarsWithdraw — вывод кристаллов покупкой Telegram Stars
 * Игрок выбирает количество звёзд и получателя (ник Telegram, по умолчанию свой),
 * экран считает сумму в кристаллах по котировке из API — так же, как бэкенд.
 */
const StarsWithdraw = () => {
  const { t } = useTranslation()
  const { balance, loadProfile } = useAppStore()
  const { info, history, isLoading, isSubmitting, error, loadData, submitStarsWithdraw, stopPolling } = useStarsWithdrawStore()

  const [stars, setStars] = useState('')
  const [username, setUsername] = useState('')
  // Новая сумма после 409 price changed — ждёт явного подтверждения игрока
  const [confirmCrystals, setConfirmCrystals] = useState(null)
  const prefilled = useRef(false)

  // Запрос экрана при каждом открытии
  useEffect(() => {
    loadData().catch(() => {})
    return () => stopPolling()
  }, [loadData, stopPolling])

  // Предзаполнение формы: минимум звёзд и свой ник
  useEffect(() => {
    if (!info || prefilled.current) return
    prefilled.current = true
    setStars(String(info.min_stars))
    setUsername(info.default_username || '')
  }, [info])

  const statusLabels = {
    new: t('starsWithdraw.status.new'),
    approved: t('starsWithdraw.status.approved'),
    rejected: t('starsWithdraw.status.rejected'),
    cancelled: t('starsWithdraw.status.cancelled'),
  }

  const getStatusConfig = (item) => {
    switch (item.status) {
      case 'new':
        return { icon: Clock, iconClass: 'text-blue-400', textClass: 'text-blue-400', containerClass: 'border-blue-500/20 bg-blue-500/5', label: statusLabels.new }
      case 'approved':
        return { icon: Loader2, iconClass: 'text-blue-400 animate-spin', textClass: 'text-blue-400', containerClass: 'border-blue-500/20 bg-blue-500/5', label: statusLabels.approved }
      case 'success':
        return { icon: CheckCircle2, iconClass: 'text-accent-primary', textClass: 'text-accent-primary', containerClass: 'border-accent-primary/20 bg-accent-primary/5', label: t('starsWithdraw.status.success', { username: item.username }) }
      case 'rejected':
      case 'cancelled':
        return { icon: XCircle, iconClass: 'text-red-400', textClass: 'text-red-400', containerClass: 'border-red-500/20 bg-red-500/5', label: statusLabels[item.status] }
      default:
        return { icon: Clock, iconClass: 'text-text-secondary', textClass: 'text-text-secondary', containerClass: 'border-white/10 bg-surface', label: item.status }
    }
  }

  if (isLoading && !info) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={32} className="text-accent-primary animate-spin" />
      </div>
    )
  }

  if (!info) {
    return (
      <div className="text-center py-16">
        <AlertCircle size={48} className="mx-auto text-text-secondary mb-4 opacity-50" />
        <p className="text-text-secondary mb-4">
          {error === 'withdraw banned' ? t('starsWithdraw.errors.withdrawBanned') : t('starsWithdraw.errors.serviceUnavailable')}
        </p>
        <button
          type="button"
          onClick={() => loadData().catch(() => {})}
          className="px-4 py-2 rounded-lg border border-white/10 text-sm text-white hover:border-accent-primary/40 transition-colors"
        >
          {t('starsWithdraw.retry')}
        </button>
      </div>
    )
  }

  // Расчёт суммы и проверки формы
  const starsNum = Number(stars)
  const starsValid = stars !== '' && Number.isInteger(starsNum) && starsNum >= info.min_stars && starsNum <= info.max_stars
  const crystals = starsValid ? starsToCrystals(starsNum, info) : null
  const recipient = normalizeUsername(username)
  const recipientValid = isValidUsername(recipient)
  const isOtherRecipient = recipientValid && recipient.toLowerCase() !== (info.default_username || '').toLowerCase()
  const notEnough = crystals !== null && crystals > balance
  const isBlocked = !info.can_withdraw || Boolean(info.active) || !info.price_micro
  const isConfirming = confirmCrystals !== null && confirmCrystals === crystals
  const canSubmit = !isBlocked && !isSubmitting && starsValid && recipientValid && crystals !== null && !notEnough

  const reasonMessages = {
    'withdraw banned': t('starsWithdraw.errors.withdrawBanned'),
    'active request': t('starsWithdraw.errors.activeRequest'),
    'service unavailable': t('starsWithdraw.errors.serviceUnavailable'),
  }
  const blockedReason = !info.can_withdraw || !info.price_micro
    ? reasonMessages[info.reason] || reasonMessages['service unavailable']
    : null

  const changeStars = (value) => {
    setStars(value)
    setConfirmCrystals(null)
  }

  const submit = async () => {
    if (!canSubmit) return
    try {
      await submitStarsWithdraw({ stars: starsNum, username: recipient, crystals })
      setConfirmCrystals(null)
      toast.success(t('starsWithdraw.success', { username: recipient }))
      // Кристаллы списаны сразу — обновляем баланс
      loadProfile().catch(() => {})
    } catch (err) {
      setConfirmCrystals(null)
      // 429 и сетевые ошибки уже показаны общим API-клиентом
      if (err?.isNotified || err?.isHandledAuthError) return

      if (err.message === 'price changed' && err.data?.quote) {
        setConfirmCrystals(err.data.crystals ?? null)
        return
      }

      const errorMap = {
        'disabled': t('starsWithdraw.errors.disabled'),
        'deposit required': t('starsWithdraw.errors.disabled'),
        'withdraw banned': t('starsWithdraw.errors.withdrawBanned'),
        'active request': t('starsWithdraw.errors.activeRequest'),
        'bad stars': t('starsWithdraw.errors.badStars', { min: formatNumber(info.min_stars), max: formatNumber(info.max_stars) }),
        'bad username': t('starsWithdraw.errors.badUsername'),
        'recipient not found': t('starsWithdraw.errors.recipientNotFound'),
        'insufficient balance': t('starsWithdraw.errors.insufficientBalance'),
        'welcome lock': t('starsWithdraw.errors.welcomeLock'),
        'recipient check failed': t('starsWithdraw.errors.recipientCheckFailed'),
        'service unavailable': t('starsWithdraw.errors.serviceUnavailable'),
        'price changed': t('starsWithdraw.errors.serviceUnavailable'),
      }
      toast.error(errorMap[err.message] || t('starsWithdraw.errors.errorBackend'))
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    submit()
  }

  const active = info.active

  return (
    <div className="space-y-6">
      {/* Активная заявка */}
      {active && (
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 size={20} className="text-blue-400 animate-spin" />
            <span className="font-semibold text-blue-400">
              {t('starsWithdraw.activeRequest')} <bdi dir="ltr">#{active.id}</bdi>
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="inline-flex items-center gap-1.5 font-bold text-white">
              <bdi dir="ltr">{formatNumber(active.stars)}</bdi> <StarIcon />
              <span className="text-text-secondary font-normal">→</span>
              <bdi dir="ltr" className="font-normal text-text-secondary">@{active.username}</bdi>
            </span>
            <span className="inline-flex items-center gap-1 text-text-secondary">
              <bdi dir="ltr">{formatNumber(active.crystals)}</bdi> <Gem size={12} className="text-accent-primary" />
            </span>
          </div>
          <p className="text-xs text-blue-400 mt-2">{statusLabels[active.status] || active.status}</p>
          <p className="text-xs text-text-secondary mt-1">{t('starsWithdraw.activeRequestDesc')}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Количество звёзд */}
        <div>
          <label htmlFor="stars-withdraw-amount" className="block text-sm font-medium text-text-secondary mb-2">
            {t('starsWithdraw.starsLabel')}
          </label>
          <div className="relative">
            <StarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" />
            <input
              id="stars-withdraw-amount"
              type="text"
              dir="ltr"
              inputMode="numeric"
              value={stars ? formatNumber(stars) : ''}
              onChange={(e) => changeStars(e.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={isBlocked || isSubmitting}
              placeholder={formatNumber(info.min_stars)}
              className="w-full bg-dark-primary border border-white border-opacity-10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
            />
          </div>
          <div className="mt-2 grid grid-cols-4 gap-2">
            {QUICK_STARS.filter((value) => value >= info.min_stars && value <= info.max_stars).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => changeStars(String(value))}
                disabled={isBlocked || isSubmitting}
                className={`py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  starsNum === value
                    ? 'bg-accent-primary/15 text-accent-primary border border-accent-primary/40'
                    : 'bg-surface text-white border border-white/10 hover:border-accent-primary/30'
                }`}
              >
                <bdi dir="ltr">{formatNumber(value)}</bdi>
              </button>
            ))}
          </div>
          <p className={`text-xs mt-1.5 ${stars !== '' && !starsValid ? 'text-red-400' : 'text-text-secondary'}`}>
            {t('starsWithdraw.limits', { min: formatNumber(info.min_stars), max: formatNumber(info.max_stars) })}
          </p>
        </div>

        {/* Получатель */}
        <div>
          <label htmlFor="stars-withdraw-recipient" className="block text-sm font-medium text-text-secondary mb-2">
            {t('starsWithdraw.recipientLabel')}
          </label>
          <div className="relative">
            <AtSign size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-primary" />
            <input
              id="stars-withdraw-recipient"
              type="text"
              dir="ltr"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value.replace(/^@/, ''))
                setConfirmCrystals(null)
              }}
              disabled={isBlocked || isSubmitting}
              placeholder="username"
              autoCapitalize="off"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              maxLength={64}
              className="w-full bg-dark-primary border border-white border-opacity-10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
            />
          </div>
          {username.trim() !== '' && !recipientValid && (
            <p className="text-xs text-red-400 mt-1.5">{t('starsWithdraw.errors.badUsername')}</p>
          )}
          {isOtherRecipient && (
            <p className="text-xs text-yellow-400 mt-1.5">
              {t('starsWithdraw.otherRecipient', { username: recipient })}
            </p>
          )}
        </div>

        {/* Сумма к списанию */}
        {crystals !== null && (
          <div className="rounded-lg bg-accent-primary/10 border border-accent-primary/20 p-4 text-center">
            <p className="text-sm text-text-secondary mb-1">{t('starsWithdraw.willDebit')}</p>
            <p className="text-3xl font-bold text-accent-primary inline-flex items-center gap-2">
              <bdi dir="ltr">{formatNumber(crystals)}</bdi> <Gem size={24} className="text-accent-primary" />
            </p>
          </div>
        )}

        {/* Причина блокировки формы */}
        {blockedReason && (
          <div className="flex items-center gap-2 text-sm text-yellow-400 rounded-lg bg-yellow-500/10 border border-yellow-500/20 p-3">
            <AlertCircle size={16} className="flex-shrink-0" />
            <span>{blockedReason}</span>
          </div>
        )}

        {isConfirming ? (
          // Подтверждение новой суммы после изменения цены звёзд
          <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/20 p-4 space-y-3">
            <p className="text-sm text-yellow-400">
              {t('starsWithdraw.priceChanged', { crystals: formatNumber(confirmCrystals) })}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setConfirmCrystals(null)}
                disabled={isSubmitting}
                className="py-3 rounded-lg border border-white/10 text-white font-semibold hover:border-white/30 transition-colors disabled:opacity-50"
              >
                {t('starsWithdraw.cancel')}
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting && <Loader2 size={18} className="animate-spin" />}
                {t('starsWithdraw.confirm')}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {t('starsWithdraw.submitting')}
              </>
            ) : notEnough ? (
              t('starsWithdraw.errors.insufficientBalance')
            ) : (
              t('starsWithdraw.submit')
            )}
          </button>
        )}

        <div className="flex items-start gap-2 text-xs text-text-secondary hidden">
          <Info size={14} className="flex-shrink-0 mt-0.5 text-accent-primary" />
          <span>{t('starsWithdraw.note')}</span>
        </div>
      </form>

      {/* История заявок */}
      {history.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-3">{t('starsWithdraw.historyTitle')}</h3>
          <div className="space-y-2">
            {history.map((item) => {
              const statusConfig = getStatusConfig(item)
              const StatusIcon = statusConfig.icon
              return (
                <div key={item.id} className={`rounded-lg border p-3 ${statusConfig.containerClass}`}>
                  <div className="flex items-center gap-3">
                    <StatusIcon size={18} className={`flex-shrink-0 ${statusConfig.iconClass}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-white">
                          <bdi dir="ltr">{formatNumber(item.stars)}</bdi> <StarIcon />
                          <bdi dir="ltr" className="text-text-secondary font-normal truncate">@{item.username}</bdi>
                        </span>
                        <bdi dir="ltr" className="text-xs text-text-secondary">#{item.id}</bdi>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-0.5 text-xs text-text-secondary">
                        <span className="inline-flex items-center gap-1">
                          <bdi dir="ltr">{formatNumber(item.crystals)}</bdi> <Gem size={12} className="text-accent-primary" />
                        </span>
                        <span>{transactionApi.formatTimeAgo(item.created_sec_ago, t)}</span>
                      </div>
                    </div>
                  </div>
                  <div className={`mt-2 pt-2 border-t border-white border-opacity-5 text-xs font-medium ${statusConfig.textClass}`}>
                    {statusConfig.label}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default StarsWithdraw
